<script setup lang="ts">
  import { computed } from 'vue'
  import type { DPIDirect, GearType } from '@rapoo/device-sdk'
  import { useDPI } from '../../composables/useDPI'
  import DPIGear from './DPIGear.vue'
  import DPISlider from './DPISlider.vue'
  import DPISkeleton from './DPISkeleton.vue'
  import { useSkeletonHold } from '../../composables/useSkeletonHold'

  const {
    dpiGears,
    currentGearId,
    phase,
    isReading,
    isReady,
    animateValueChanges,
    setCurrentGear,
    setGearDPI,
    setGears,
  } = useDPI()

  const showSkeleton = computed(() => phase.value === 'cold' && isReading.value)
  const skeletonVisible = useSkeletonHold(showSkeleton)
  const currentGear = computed(() => dpiGears.value.find((item) => item.id === currentGearId.value))

  const handleChangeGear = async (gear: GearType) => {
    if (!isReady.value) return

    const index = dpiGears.value.findIndex((item) => item.id === gear.id)
    if (index === -1) return

    await setCurrentGear(index)
  }

  const handleChangeGears = async (gears: GearType[]) => {
    if (!isReady.value) return
    await setGears(gears)
  }

  const handleChangeIndependent = async (val: boolean) => {
    if (!isReady.value || !currentGear.value) return

    const gears = dpiGears.value.map((item) => {
      if (item.id !== currentGear.value?.id) return item

      return {
        ...item,
        y: val ? item.y : item.x,
        independentXY: val,
      }
    })

    await setGears(gears)
  }

  const handleChangeDpi = async (val: { x: number; y: number }) => {
    if (!isReady.value) return

    const gear = currentGear.value
    if (!gear) return

    const index = dpiGears.value.findIndex((item) => item.id === currentGear.value?.id)
    if (index === -1) return

    const directs: DPIDirect[] = gear.independentXY
      ? (['x', 'y'] as const).filter((direct) => val[direct] !== gear[direct])
      : ['x', 'y']

    if (!directs.length) return

    // 先一次性更新本地档位状态，避免 x/y 指令逐个返回时
    // currentGear 出现「新 x + 旧 y」的中间态，打断滑块的过渡动画
    dpiGears.value[index] = { ...gear, x: val.x, y: val.y }

    for (const direct of directs) {
      await setGearDPI(index, val[direct], direct)
    }
  }
</script>

<template>
  <Transition
    mode="out-in"
    enter-active-class="animate-in fade-in zoom-in-99 duration-200"
    leave-active-class="animate-out fade-out zoom-out-99 duration-200"
  >
    <DPISkeleton v-if="skeletonVisible" key="skeleton" />
    <div
      v-else-if="phase === 'error'"
      key="error"
      class="flex h-67.5 w-full items-center justify-center text-m-medium text-text-glass-default"
    >
      {{ $t('dpi.loadFailed') }}
    </div>
    <div
      v-else-if="currentGear"
      key="content"
      class="flex w-full items-center justify-center gap-2"
    >
      <DPIGear
        :dpi-gears="dpiGears"
        :current-gear-id="currentGearId"
        @gear-change="handleChangeGear"
        @gears-change="handleChangeGears"
      />
      <DPISlider
        :current="currentGear"
        :animate="animateValueChanges"
        @independent-change="handleChangeIndependent"
        @dpi-change="handleChangeDpi"
      />
    </div>
  </Transition>
</template>
